import appConfig from '@/config/appConfig.typed';
import {PUSH_NOTIFICATIONS_PROMPT_STATUSES as PROMPT_STATUSES} from '@/consts/pushNotifications/pushNotificationsPromptStatuses';
import {PUSH_NOTIFICATIONS_PROMPT_TYPES} from '@/consts/pushNotifications/pushNotificationsPromptTypes';
import appContent from '@/services/content/appContent';
import notificationPermissionService from '@/services/pushNotification/notificationPermissionService';
import pushNotificationService from '@/services/pushNotification/pushNotificationService';
import {showEnableNotificationReminderModal} from '@/state/slices/modalSlice';
import {dispatch} from '@/state/store';
import dateTimeUtils from '@/utils/dateTimeUtils';
import objUtils from '@/utils/objUtils';

import notificationStorageService from '../storage/notificationStorageService';

const isPromptAvailable = async (pushNotificationPromptData, promptType) => {
    const returnWitReset = (isAvailable, isNeedReset) => {
        return {isAvailable, isNeedReset};
    };

    const promptPopup = appContent.getNotificationPromptPopup() || {};

    //no content - no prompt
    if (!promptPopup) return returnWitReset(false, true);

    const isPushNotificationSupported = await pushNotificationService.checkIfPushNotificationSupported();

    // Push notifications are not supported in the browser
    if (!isPushNotificationSupported) return returnWitReset(false, true);

    const {statuses = {}} = pushNotificationPromptData;
    const shownCount = Object.values(statuses).filter((value) => value === PROMPT_STATUSES.PROMPT_SHOWN).length;
    const maxPossiblePromptCount = appConfig.getMaxPossiblePromptCount();

    //max number of prompts already shown
    if (shownCount - maxPossiblePromptCount >= 0) return returnWitReset(false, true);

    const isGranted = await notificationPermissionService.checkIfNotificationIsGranted();

    if (isGranted) {
        const isPushNotificationDisabled = notificationStorageService.getIsPushNotificationDisabled();

        //if user unsubscribed manually (isPushNotificationDisabled will be true) and promptType is service event - prompt needed
        if (isPushNotificationDisabled && promptType !== PUSH_NOTIFICATIONS_PROMPT_TYPES.REGULAR) {
            return returnWitReset(true);
        }

        //reset flow only if user not turned off notifications so autosubscribe will be done in background
        if (!isPushNotificationDisabled) return returnWitReset(false, true);

        return returnWitReset(false);
    }

    return returnWitReset(true);
};

const isPromptAvailableWithReset = async (pushNotificationPromptData, promptType) => {
    const {isAvailable, isNeedReset} = await isPromptAvailable(pushNotificationPromptData, promptType);

    if (isNeedReset) {
        resetPushPrompt();
    }

    return isAvailable;
};

const getPushNotificationsPromptData = () => {
    try {
        const pushNotificationPromptData = notificationStorageService.getPushNotificationPromptDataToLocalStorage();

        return JSON.parse(pushNotificationPromptData) || {};
    } catch (error) {
        //ignored
        return {};
    }
};

const schedulePushPromptIfNeeded = async (promptType) => {
    const pushNotificationPromptData = getPushNotificationsPromptData();
    const isPromptAvailable = await isPromptAvailableWithReset(pushNotificationPromptData, promptType);

    if (!isPromptAvailable) return;

    const {promptStartDate, promptShowDate, statuses = {}} = pushNotificationPromptData;

    //already some prompt type is added to show
    if (promptStartDate) {
        await checkIsShowPushPrompt();
        return;
    }

    const status = statuses[promptType];

    //already triggered
    if (status === PROMPT_STATUSES.TRIGGERED || status === PROMPT_STATUSES.PROMPT_SHOWN) {
        await checkIsShowPushPrompt();
        return;
    }

    statuses[promptType] = PROMPT_STATUSES.TRIGGERED;

    const data = {
        statuses,
        promptStartDate: new Date(),
        promptShowDate,
    };

    notificationStorageService.setPushNotificationPromptDataToLocalStorage(JSON.stringify(data));

    await checkIsShowPushPrompt();
};

const showPrompt = (promptType) => {
    const pushNotificationPromptData = getPushNotificationsPromptData();
    const {statuses = {}} = pushNotificationPromptData;
    const status = statuses[promptType];

    //status should be TRIGGERED to show prompt
    if (status !== PROMPT_STATUSES.TRIGGERED) return;

    statuses[promptType] = PROMPT_STATUSES.PROMPT_SHOWN;

    const data = {
        statuses,
        promptStartDate: undefined,
        promptShowDate: new Date(),
    };

    notificationStorageService.setPushNotificationPromptDataToLocalStorage(JSON.stringify(data));

    dispatch(showEnableNotificationReminderModal({promptType}));
};

let promptTimeout;

const checkIsShowPushPrompt = async () => {
    clearTimeout(promptTimeout);

    const pushNotificationPromptData = getPushNotificationsPromptData();
    const isPromptAvailable = await isPromptAvailableWithReset(pushNotificationPromptData);

    if (!isPromptAvailable) return;

    const {promptStartDate, promptShowDate, statuses = {}} = pushNotificationPromptData;

    //if no promptStartDate then prompt popup can't be shown
    if (!promptStartDate) return;

    const promptType = Object.entries(statuses)
        .filter(([, value]) => value === PROMPT_STATUSES.TRIGGERED)
        .map(([key]) => key)[0];

    //prompt type should be defined and only one
    if (!promptType) return;

    const timeout = getPromptTimeout(promptType, promptStartDate, promptShowDate);

    //if no timeout show prompt immediately
    if (timeout <= 0) {
        showPrompt(promptType);
        return;
    }

    promptTimeout = setTimeout(() => {
        checkIsShowPushPrompt();
    }, timeout);
};

const getPromptTimeout = (promptType, promptStartDate, promptShowDate) => {
    //number of days after event was clicked
    const promptShowAfterDays = getPromptAfterDays(promptType);
    //next prompt date
    const promptNextShowDate = dateTimeUtils.addDaysToDate(promptStartDate, promptShowAfterDays);
    const minimumAfterPreviousPromptDays = appConfig.getMinimumAfterPreviousPromptDays();
    //next minimum date
    const minimumNextShowDate = dateTimeUtils.addDaysToDate(promptShowDate, minimumAfterPreviousPromptDays) || 0;
    //choose older from prompt date and minimum date
    const nextShowDate = minimumNextShowDate > promptNextShowDate ? minimumNextShowDate : promptNextShowDate;

    return nextShowDate - new Date();
};

const getPromptAfterDays = (promptType) => {
    switch (promptType) {
        case PUSH_NOTIFICATIONS_PROMPT_TYPES.LOYALTY_CLICK:
            return appConfig.getShowPromptAfterLoyaltyClickDays();
        case PUSH_NOTIFICATIONS_PROMPT_TYPES.AUTODELIVERY_DASHBOARD_CLICK:
            return appConfig.getShowPromptAfterAutodeliveryDashboardClickDays();
        case PUSH_NOTIFICATIONS_PROMPT_TYPES.URL_OPEN_ON_SHOP_PAGE:
            return appConfig.getShowPromptAfterUrlClickOnShopPageDays();
        case PUSH_NOTIFICATIONS_PROMPT_TYPES.REGULAR:
            return appConfig.getShowPromptIfLoggedInDays();
        default:
            return 0;
    }
};

const resetPushPrompt = () => {
    clearTimeout(promptTimeout);

    const pushNotificationPromptData = getPushNotificationsPromptData();
    const {promptShowDate, statuses = {}} = pushNotificationPromptData;

    const data = {
        statuses: objUtils.filterKeysByValue(statuses, PROMPT_STATUSES.PROMPT_SHOWN),
        promptStartDate: undefined,
        promptShowDate,
    };

    notificationStorageService.setPushNotificationPromptDataToLocalStorage(JSON.stringify(data));
};

export {checkIsShowPushPrompt, schedulePushPromptIfNeeded};
